import { useState, useEffect, useCallback, useMemo } from 'react';
import { crmService } from '../../services/crmService';
import { useIsHQ } from '../../hooks/useIsHQ';

// ─── Constants ─────────────────────────────────────────────────────────────────

const CAT_META = {
  acronym:      { color: '#22D3EE', label: 'Acronym' },
  term:         { color: '#3B82F6', label: 'Term' },
  concept:      { color: '#8B5CF6', label: 'Concept' },
  program:      { color: '#10B981', label: 'Program' },
  organization: { color: '#F59E0B', label: 'Organization' },
};

const ALIGNMENT_COLOR = {
  five_eyes:  '#22D3EE', nato_ally: '#3B82F6', eu_partner: '#06B6D4',
  friendly: '#10B981',   neutral: '#6B7280',   unfriendly: '#F59E0B', adversary: '#EF4444',
};

function CategoryBadge({ category }) {
  const m = CAT_META[category] || { color: '#6B7280', label: category || '—' };
  return (
    <span style={{
      background: m.color + '22', color: m.color,
      border: `1px solid ${m.color}44`, borderRadius: 4,
      padding: '2px 7px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
}

function VerticalChip({ name }) {
  if (!name) return null;
  return (
    <span style={{
      background: '#1E2D45', color: '#94A3B8', border: '1px solid #374151',
      borderRadius: 4, padding: '2px 7px', fontSize: 10, whiteSpace: 'nowrap',
    }}>
      {name}
    </span>
  );
}

function getDomain(url) {
  if (!url) return null;
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, '');
  } catch { return null; }
}

// ─── Shared form primitives ────────────────────────────────────────────────────

const inp = {
  width: '100%', background: '#0A0F1C', border: '1px solid #1E2D45',
  borderRadius: 6, padding: '7px 10px', color: '#E2E8F0', fontSize: 13,
  boxSizing: 'border-box', outline: 'none',
};

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 11, color: '#94A3B8', fontWeight: 600, marginBottom: 4, letterSpacing: '0.05em' }}>
        {label.toUpperCase()}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, width }) {
  const w = Math.min(width || 580, window.innerWidth - 40);
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 10, width: w, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #1E2D45', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, color: '#F1F5F9', fontSize: 14 }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

const btnCancel = { background: 'transparent', color: '#94A3B8', border: '1px solid #374151', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 12 };
const btnSave   = { background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 6, padding: '7px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 700 };

// ─── Term add/edit modal ────────────────────────────────────────────────────────

function TermModal({ term, verticals, onClose, onSave }) {
  const isEdit = !!term;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [form, setForm] = useState({
    term:        term?.term || '',
    acronym:     term?.acronym || '',
    definition:  term?.definition || '',
    category:    term?.category || 'acronym',
    vertical_id: term?.vertical_id || '',
    source:      term?.source || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.term.trim() || !form.definition.trim()) { setError('Term and definition are required'); return; }
    setSaving(true); setError(null);
    try {
      const payload = { ...form, vertical_id: form.vertical_id ? parseInt(form.vertical_id) : null };
      isEdit
        ? await crmService.updateKnowledgeTerm(term.id, payload)
        : await crmService.createKnowledgeTerm(payload);
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? `Edit: ${term.term}` : 'Add Term'} onClose={onClose} width={600}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0 16px' }}>
        <Field label="Term" required><input style={inp} value={form.term} onChange={e => set('term', e.target.value)} /></Field>
        <Field label="Acronym"><input style={inp} value={form.acronym} onChange={e => set('acronym', e.target.value)} placeholder="e.g. AESA" /></Field>
        <Field label="Category">
          <select style={inp} value={form.category} onChange={e => set('category', e.target.value)}>
            {['acronym','term','concept','program','organization'].map(c => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
        </Field>
        <Field label="Vertical">
          <select style={inp} value={form.vertical_id} onChange={e => set('vertical_id', e.target.value)}>
            <option value="">— none —</option>
            {verticals.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Definition" required>
        <textarea style={{ ...inp, height: 100, resize: 'vertical' }} value={form.definition} onChange={e => set('definition', e.target.value)} />
      </Field>
      <Field label="Source">
        <input style={inp} value={form.source} onChange={e => set('source', e.target.value)} placeholder="DoD Dictionary, FAR, 10 USC, etc." />
      </Field>
      {error && <div style={{ color: '#EF4444', fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={btnCancel}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ ...btnSave, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Term')}
        </button>
      </div>
    </Modal>
  );
}

// ─── GLOSSARY TAB ──────────────────────────────────────────────────────────────

function GlossaryTab({ verticals }) {
  const isHQ = useIsHQ();
  const [terms, setTerms]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTerms(await crmService.getKnowledgeTerms()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return terms.filter(t =>
      (!catFilter || t.category === catFilter) &&
      (!q || (t.acronym || '').toLowerCase().includes(q) || t.term.toLowerCase().includes(q) || t.definition.toLowerCase().includes(q))
    );
  }, [terms, search, catFilter]);

  const handleDelete = async (id) => {
    if (confirmDel !== id) { setConfirmDel(id); return; }
    try { await crmService.deleteKnowledgeTerm(id); setConfirmDel(null); load(); }
    catch (e) { alert(e.message); }
  };

  const afterSave = () => { setModalOpen(false); setEditTarget(null); load(); };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search acronym, term, or definition…"
          style={{ ...inp, width: 280, flex: 'none' }}
        />
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={{ ...inp, width: 140, flex: 'none' }}>
          <option value="">All categories</option>
          {Object.entries(CAT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={() => { setEditTarget(null); setModalOpen(true); }}
            style={{ background: '#0D1F3C', color: '#22D3EE', border: '1px solid #22D3EE44', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
          >
            + Add Term
          </button>
        </div>
      </div>

      {loading ? <div style={{ color: '#64748B', padding: 20 }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1E2D45' }}>
                {['Acronym','Term','Category','Vertical','Definition','Actions'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#64748B', fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid #0D1117', verticalAlign: 'top' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#0D1F3C22'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: '#22D3EE', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {t.acronym || '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#E2E8F0', fontWeight: 500, maxWidth: 180 }}>{t.term}</td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}><CategoryBadge category={t.category} /></td>
                  <td style={{ padding: '10px 12px' }}><VerticalChip name={t.vertical_name} /></td>
                  <td style={{ padding: '10px 12px', color: '#94A3B8', lineHeight: 1.5, maxWidth: 340 }}>{t.definition}</td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => { setEditTarget(t); setModalOpen(true); }}
                        style={{ background: 'transparent', color: '#94A3B8', border: '1px solid #374151', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11 }}
                      >
                        Edit
                      </button>
                      {isHQ && (
                        <button
                          onClick={() => handleDelete(t.id)}
                          onBlur={() => setTimeout(() => setConfirmDel(null), 200)}
                          style={{ background: confirmDel === t.id ? '#EF444422' : 'transparent', color: confirmDel === t.id ? '#EF4444' : '#64748B', border: `1px solid ${confirmDel === t.id ? '#EF4444' : '#374151'}`, borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11 }}
                        >
                          {confirmDel === t.id ? 'Confirm?' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, color: '#64748B', textAlign: 'center' }}>
                  {search || catFilter ? 'No terms match.' : 'No terms yet. Add one above.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <TermModal term={editTarget} verticals={verticals} onClose={() => { setModalOpen(false); setEditTarget(null); }} onSave={afterSave} />
      )}
    </div>
  );
}

// ─── VERTICALS TAB ─────────────────────────────────────────────────────────────

function VerticalsTab({ verticals }) {
  const [terms, setTerms]           = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null); // vertical id

  useEffect(() => {
    Promise.all([
      crmService.getKnowledgeTerms(),
      crmService.getCompetitors(),
    ]).then(([t, c]) => { setTerms(t); setCompetitors(c); }).catch(console.error).finally(() => setLoading(false));
  }, []);

  // Build lookup maps
  const termsByVertical = useMemo(() => {
    const map = {};
    terms.forEach(t => { if (t.vertical_id) { (map[t.vertical_id] = map[t.vertical_id] || []).push(t); } });
    return map;
  }, [terms]);

  const competitorsByVertical = useMemo(() => {
    const map = {};
    competitors.forEach(c => {
      (c.verticals || []).forEach(v => {
        const vid = v.id || v;
        (map[vid] = map[vid] || []).push(c);
      });
    });
    return map;
  }, [competitors]);

  const selectedVertical = selected ? verticals.find(v => v.id === selected) : null;
  const selectedTerms    = selected ? (termsByVertical[selected] || [])    : [];
  const selectedComps    = selected ? (competitorsByVertical[selected] || []) : [];

  if (loading) return <div style={{ color: '#64748B', padding: 20 }}>Loading…</div>;

  return (
    <div>
      {/* Card grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginBottom: selected ? 20 : 0 }}>
        {verticals.map(v => {
          const termCount = (termsByVertical[v.id] || []).length;
          const compCount = (competitorsByVertical[v.id] || []).length;
          const isSelected = selected === v.id;
          return (
            <div
              key={v.id}
              onClick={() => setSelected(isSelected ? null : v.id)}
              style={{
                background: isSelected ? '#0D1F3C' : '#111827',
                border: `1px solid ${isSelected ? '#22D3EE' : '#1E2D45'}`,
                borderTop: `3px solid ${isSelected ? '#22D3EE' : '#1E2D45'}`,
                borderRadius: 8, padding: '14px 16px', cursor: 'pointer',
                transition: 'border-color 0.15s',
              }}
            >
              <div style={{ fontWeight: 700, color: '#F1F5F9', fontSize: 13, marginBottom: 6 }}>{v.name}</div>
              {v.customer_problem && (
                <div style={{ color: '#94A3B8', fontSize: 11, lineHeight: 1.5, marginBottom: 10, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {v.customer_problem}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 'auto' }}>
                {compCount > 0 && (
                  <span style={{ background: '#EF444422', color: '#EF4444', border: '1px solid #EF444444', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 600 }}>
                    {compCount} competitor{compCount !== 1 ? 's' : ''}
                  </span>
                )}
                {termCount > 0 && (
                  <span style={{ background: '#22D3EE22', color: '#22D3EE', border: '1px solid #22D3EE44', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 600 }}>
                    {termCount} term{termCount !== 1 ? 's' : ''}
                  </span>
                )}
                {compCount === 0 && termCount === 0 && (
                  <span style={{ color: '#374151', fontSize: 10 }}>no data yet</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail panel */}
      {selectedVertical && (
        <div style={{ background: '#111827', border: '1px solid #22D3EE44', borderRadius: 10, padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ margin: 0, color: '#22D3EE', fontSize: 16, fontWeight: 700 }}>{selectedVertical.name}</h3>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: 18 }}>×</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0 32px', marginBottom: 20 }}>
            {selectedVertical.customer_problem && (
              <div>
                <div style={{ fontSize: 10, color: '#64748B', fontWeight: 700, marginBottom: 6, letterSpacing: '0.06em' }}>CUSTOMER PROBLEM</div>
                <div style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.6 }}>{selectedVertical.customer_problem}</div>
              </div>
            )}
            {selectedVertical.key_buyers && (
              <div>
                <div style={{ fontSize: 10, color: '#64748B', fontWeight: 700, marginBottom: 6, letterSpacing: '0.06em' }}>KEY BUYERS</div>
                <div style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.6 }}>{selectedVertical.key_buyers}</div>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0 32px' }}>
            {/* Competitors */}
            <div>
              <div style={{ fontSize: 10, color: '#64748B', fontWeight: 700, marginBottom: 10, letterSpacing: '0.06em' }}>
                COMPETITORS ({selectedComps.length})
              </div>
              {selectedComps.length === 0 ? (
                <div style={{ color: '#374151', fontSize: 12 }}>None tracked yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selectedComps.slice(0, 12).map(c => {
                    const domain = getDomain(c.website);
                    const alignColor = ALIGNMENT_COLOR[c.country_alignment] || '#6B7280';
                    return (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {domain ? (
                          <img
                            src={`https://logo.clearbit.com/${domain}`}
                            alt=""
                            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            style={{ width: 22, height: 22, borderRadius: 4, objectFit: 'contain', background: '#1E2D45', flexShrink: 0 }}
                          />
                        ) : null}
                        <div style={{ display: 'none', width: 22, height: 22, borderRadius: 4, background: '#1E2D45', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#94A3B8', fontWeight: 700, flexShrink: 0 }}>
                          {(c.name || '?')[0]}
                        </div>
                        <span style={{ fontSize: 12, color: '#E2E8F0', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                        {c.country_alignment && (
                          <span style={{ background: alignColor + '22', color: alignColor, border: `1px solid ${alignColor}44`, borderRadius: 3, padding: '1px 5px', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                            {c.country_alignment.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {selectedComps.length > 12 && (
                    <div style={{ fontSize: 11, color: '#64748B' }}>+{selectedComps.length - 12} more</div>
                  )}
                </div>
              )}
            </div>

            {/* Knowledge terms */}
            <div>
              <div style={{ fontSize: 10, color: '#64748B', fontWeight: 700, marginBottom: 10, letterSpacing: '0.06em' }}>
                GLOSSARY TERMS ({selectedTerms.length})
              </div>
              {selectedTerms.length === 0 ? (
                <div style={{ color: '#374151', fontSize: 12 }}>No terms linked to this vertical.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {selectedTerms.map(t => (
                    <div key={t.id}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        {t.acronym && <span style={{ color: '#22D3EE', fontWeight: 700, fontSize: 12 }}>{t.acronym}</span>}
                        <span style={{ color: '#E2E8F0', fontSize: 12 }}>{t.term}</span>
                        <CategoryBadge category={t.category} />
                      </div>
                      <div style={{ color: '#64748B', fontSize: 11, lineHeight: 1.5 }}>{t.definition.slice(0, 120)}{t.definition.length > 120 ? '…' : ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ACRONYM FINDER TAB ────────────────────────────────────────────────────────

function AcronymFinderTab() {
  const [terms, setTerms]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery]   = useState('');

  useEffect(() => {
    crmService.getKnowledgeTerms().then(setTerms).catch(console.error).finally(() => setLoading(false));
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return terms.filter(t => t.acronym);
    return terms.filter(t =>
      (t.acronym || '').toLowerCase().includes(q) || t.term.toLowerCase().includes(q)
    );
  }, [terms, query]);

  return (
    <div style={{ maxWidth: 700 }}>
      {/* Search */}
      <div style={{ marginBottom: 24 }}>
        <input
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Type an acronym or keyword…"
          style={{
            ...inp,
            fontSize: 18, padding: '14px 18px',
            borderColor: query ? '#22D3EE' : '#1E2D45',
            boxShadow: query ? '0 0 0 2px #22D3EE22' : 'none',
          }}
        />
        {query && <div style={{ color: '#64748B', fontSize: 11, marginTop: 6 }}>{results.length} result{results.length !== 1 ? 's' : ''}</div>}
      </div>

      {loading ? (
        <div style={{ color: '#64748B' }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {results.map(t => (
            <div
              key={t.id}
              style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 8, padding: '14px 18px' }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ color: '#22D3EE', fontWeight: 800, fontSize: 18, fontFamily: 'monospace' }}>
                  {t.acronym || t.term.slice(0, 4).toUpperCase()}
                </span>
                <span style={{ color: '#E2E8F0', fontSize: 14, fontWeight: 500 }}>{t.term}</span>
                <CategoryBadge category={t.category} />
                {t.vertical_name && <VerticalChip name={t.vertical_name} />}
              </div>
              <div style={{ color: '#94A3B8', fontSize: 13, lineHeight: 1.6 }}>{t.definition}</div>
              {t.source && <div style={{ color: '#4B5563', fontSize: 11, marginTop: 6 }}>Source: {t.source}</div>}
            </div>
          ))}
          {!loading && results.length === 0 && query && (
            <div style={{ color: '#64748B', textAlign: 'center', padding: 32 }}>No matches for "{query}"</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────

export default function KnowledgeBase() {
  const [tab, setTab]           = useState('glossary');
  const [verticals, setVerticals] = useState([]);

  useEffect(() => {
    crmService.getVerticals().then(setVerticals).catch(console.error);
  }, []);

  const TABS = [
    ['glossary', 'Glossary'],
    ['verticals', 'Verticals'],
    ['finder', 'Acronym Finder'],
  ];

  return (
    <div style={{ padding: '0 24px 32px', fontFamily: 'inherit' }}>
      {/* Tab nav */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1E2D45', marginBottom: 20 }}>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '10px 20px', background: 'none', border: 'none',
            borderBottom: tab === k ? '2px solid #22D3EE' : '2px solid transparent',
            color: tab === k ? '#22D3EE' : '#6B7280',
            cursor: 'pointer', fontSize: 13, fontWeight: tab === k ? 600 : 400,
            marginBottom: -1,
          }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'glossary'  && <GlossaryTab verticals={verticals} />}
      {tab === 'verticals' && <VerticalsTab verticals={verticals} />}
      {tab === 'finder'    && <AcronymFinderTab />}
    </div>
  );
}
